/**
 * App 管理 API 服务
 * 
 * 包含 IAM v3 API（App 管理）和 Admin v3 API（分类管理、项目发布）
 */

import axios, { AxiosInstance } from 'axios'
import type { SuccessResponse } from '@/shared/types/api'
import { TokenManager } from '@/shared/services/api'
import type {
  App,
  AppListData,
  CreateAppRequest,
  UpdateAppRequest,
  Category,
  CategoryListData,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  ReorderCategoriesRequest,
  ReorderData,
  ProjectListData,
  ProjectPublishStatus,
  PublishProjectRequest,
  PublishProjectData,
  UnpublishProjectData,
  IconUploadRequest,
  IconUploadResponse,
  DeleteData,
  ProjectListWithAppStatusData,
  // v11.2.2 新增
  AvatarUploadRequest,
  AvatarUploadResponse,
} from '../types/app'

// ==================== v3 API 客户端配置 ====================

/**
 * v3 API 基础 URL 配置
 * 
 * 环境变量：
 * - VITE_IAM_V3_API_URL: IAM v3 API（App 管理）
 * - VITE_AGENT_ADMIN_V3_API_URL: Admin v3 API（分类管理、项目发布）
 * - VITE_AGENT_RUNTIME_V3_API_URL: Runtime v3 API（App 端业务，Console 暂不使用）
 * 
 * 本地开发默认值：
 * - IAM: http://localhost:8000/api/v3/iam
 * - Admin: http://localhost:8001/api/v3/admin
 * - Runtime: http://localhost:8002/api/v3/runtime
 */
const API_V3_URLS = {
  iam: import.meta.env.VITE_IAM_V3_API_URL || 'http://localhost:8000/api/v3/iam',
  agentAdmin: import.meta.env.VITE_AGENT_ADMIN_V3_API_URL || 'http://localhost:8001/api/v3/admin',
  agentRuntime: import.meta.env.VITE_AGENT_RUNTIME_V3_API_URL || 'http://localhost:8002/api/v3/runtime',
}

/**
 * 获取 v3 API 基础 URL
 */
function getV3BaseUrl(service: 'iam' | 'agentAdmin' | 'agentRuntime'): string {
  return API_V3_URLS[service]
}

/**
 * 创建带有认证的 Axios 实例
 */
function createV3Client(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // 请求拦截器：注入 Token
  client.interceptors.request.use(
    (config) => {
      const token = TokenManager.getToken()
      console.log('🔑 [App API v3] Token check:', {
        hasToken: !!token,
        tokenPrefix: token ? token.substring(0, 20) + '...' : null,
        url: config.url,
      })
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        console.warn('⚠️ [App API v3] No token found in localStorage!')
      }
      config.headers['X-Request-ID'] = crypto.randomUUID()
      return config
    },
    (error) => Promise.reject(error)
  )

  // 响应拦截器：日志记录
  client.interceptors.response.use(
    (response) => {
      console.log('📥 [App API v3] Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      })
      return response
    },
    (error) => {
      console.error('❌ [App API v3] Error:', {
        status: error.response?.status,
        url: error.config?.url,
        data: error.response?.data,
      })
      return Promise.reject(error)
    }
  )

  return client
}

// 创建 v3 客户端实例
const iamV3Client = createV3Client(getV3BaseUrl('iam'))
const adminV3Client = createV3Client(getV3BaseUrl('agentAdmin'))

// ==================== IAM v3 API - App 管理 ====================

export class AppApiService {
  /**
   * 获取 App 列表
   * GET /api/v3/apps
   */
  static async getApps(params?: {
    page?: number
    page_size?: number
  }): Promise<SuccessResponse<AppListData>> {
    const response = await iamV3Client.get<SuccessResponse<AppListData>>('/apps', { params })
    return response.data
  }

  /**
   * 获取 App 详情
   * GET /api/v3/apps/{app_id}
   */
  static async getApp(appId: string): Promise<SuccessResponse<App>> {
    const response = await iamV3Client.get<SuccessResponse<App>>(`/apps/${appId}`)
    return response.data
  }

  /**
   * 创建 App
   * POST /api/v3/apps
   */
  static async createApp(data: CreateAppRequest): Promise<SuccessResponse<App>> {
    const response = await iamV3Client.post<SuccessResponse<App>>('/apps', data)
    return response.data
  }

  /**
   * 更新 App
   * PUT /api/v3/apps/{app_id}
   */
  static async updateApp(appId: string, data: UpdateAppRequest): Promise<SuccessResponse<App>> {
    const response = await iamV3Client.put<SuccessResponse<App>>(`/apps/${appId}`, data)
    return response.data
  }

  /**
   * 删除 App
   * DELETE /api/v3/apps/{app_id}
   */
  static async deleteApp(appId: string): Promise<SuccessResponse<DeleteData>> {
    const response = await iamV3Client.delete<SuccessResponse<DeleteData>>(`/apps/${appId}`)
    return response.data
  }

  /**
   * 获取 App 图标上传 URL
   * POST /api/v3/apps/{app_id}/icon/upload-url
   */
  static async getAppIconUploadUrl(
    appId: string,
    data: IconUploadRequest
  ): Promise<SuccessResponse<IconUploadResponse>> {
    const response = await iamV3Client.post<SuccessResponse<IconUploadResponse>>(
      `/apps/${appId}/icon/upload-url`,
      data
    )
    return response.data
  }

  /**
   * 获取分类图标上传 URL
   * POST /api/v3/icons/categories/{app_id}/{category_id}/upload-url
   */
  static async getCategoryIconUploadUrl(
    appId: string,
    categoryId: string,
    data: IconUploadRequest
  ): Promise<SuccessResponse<IconUploadResponse>> {
    const response = await iamV3Client.post<SuccessResponse<IconUploadResponse>>(
      `/icons/categories/${appId}/${categoryId}/upload-url`,
      data
    )
    return response.data
  }
}

// ==================== Admin v3 API - 分类管理 ====================

export class CategoryApiService {
  /**
   * 获取分类列表
   * GET /api/v3/apps/{app_id}/categories
   */
  static async getCategories(appId: string): Promise<SuccessResponse<CategoryListData>> {
    const response = await adminV3Client.get<SuccessResponse<CategoryListData>>(
      `/apps/${appId}/categories`
    )
    return response.data
  }

  /**
   * 创建分类
   * POST /api/v3/apps/{app_id}/categories
   */
  static async createCategory(
    appId: string,
    data: CreateCategoryRequest
  ): Promise<SuccessResponse<Category>> {
    const response = await adminV3Client.post<SuccessResponse<Category>>(
      `/apps/${appId}/categories`,
      data
    )
    return response.data
  }

  /**
   * 更新分类
   * PUT /api/v3/apps/{app_id}/categories/{category_id}
   */
  static async updateCategory(
    appId: string,
    categoryId: string,
    data: UpdateCategoryRequest
  ): Promise<SuccessResponse<Category>> {
    const response = await adminV3Client.put<SuccessResponse<Category>>(
      `/apps/${appId}/categories/${categoryId}`,
      data
    )
    return response.data
  }

  /**
   * 删除分类
   * DELETE /api/v3/apps/{app_id}/categories/{category_id}
   */
  static async deleteCategory(
    appId: string,
    categoryId: string
  ): Promise<SuccessResponse<DeleteData>> {
    const response = await adminV3Client.delete<SuccessResponse<DeleteData>>(
      `/apps/${appId}/categories/${categoryId}`
    )
    return response.data
  }

  /**
   * 批量排序分类
   * PATCH /api/v3/apps/{app_id}/categories/reorder
   */
  static async reorderCategories(
    appId: string,
    data: ReorderCategoriesRequest
  ): Promise<SuccessResponse<ReorderData>> {
    const response = await adminV3Client.patch<SuccessResponse<ReorderData>>(
      `/apps/${appId}/categories/reorder`,
      data
    )
    return response.data
  }
}

// ==================== Admin v3 API - 项目发布 ====================

export class ProjectPublishApiService {
  /**
   * 获取项目列表（含 App 发布状态）
   * GET /api/v3/admin/projects
   * 
   * v11.2.1 新增：用于 Dashboard 项目卡片显示 App 发布状态
   */
  static async getProjectsWithAppStatus(
    params?: {
      page?: number
      page_size?: number
    }
  ): Promise<SuccessResponse<ProjectListWithAppStatusData>> {
    const response = await adminV3Client.get<SuccessResponse<ProjectListWithAppStatusData>>(
      '/projects',
      { params }
    )
    return response.data
  }

  /**
   * 获取 App 下的项目列表
   * GET /api/v3/apps/{app_id}/projects
   */
  static async getAppProjects(
    appId: string,
    params?: {
      category?: string
      search?: string
      page?: number
      page_size?: number
    }
  ): Promise<SuccessResponse<ProjectListData>> {
    const response = await adminV3Client.get<SuccessResponse<ProjectListData>>(
      `/apps/${appId}/projects`,
      { params }
    )
    return response.data
  }

  /**
   * 获取项目发布状态
   * GET /api/v3/projects/{project_id}/app-status
   */
  static async getProjectAppStatus(
    projectId: string
  ): Promise<SuccessResponse<ProjectPublishStatus>> {
    const response = await adminV3Client.get<SuccessResponse<ProjectPublishStatus>>(
      `/projects/${projectId}/app-status`
    )
    return response.data
  }

  /**
   * 发布项目到 App
   * PUT /api/v3/projects/{project_id}/publish-to-app
   */
  static async publishProject(
    projectId: string,
    data: PublishProjectRequest
  ): Promise<SuccessResponse<PublishProjectData>> {
    const response = await adminV3Client.put<SuccessResponse<PublishProjectData>>(
      `/projects/${projectId}/publish-to-app`,
      data
    )
    return response.data
  }

  /**
   * 取消发布项目
   * DELETE /api/v3/projects/{project_id}/publish-to-app
   * 
   * v11.2.2 修复：需要传 app_id 指定取消发布的目标 App
   */
  static async unpublishProject(
    projectId: string,
    appId: string
  ): Promise<SuccessResponse<UnpublishProjectData>> {
    const response = await adminV3Client.delete<SuccessResponse<UnpublishProjectData>>(
      `/projects/${projectId}/publish-to-app`,
      { data: { app_id: appId } }
    )
    return response.data
  }

  /**
   * 获取头像上传 URL
   * POST /api/v3/admin/uploads/avatar
   * 
   * v11.2.2 新增：发布项目到 App 前需要先上传角色头像
   */
  static async getAvatarUploadUrl(
    data: AvatarUploadRequest
  ): Promise<SuccessResponse<AvatarUploadResponse>> {
    const response = await adminV3Client.post<SuccessResponse<AvatarUploadResponse>>(
      '/uploads/avatar',
      data
    )
    return response.data
  }
}

// ==================== 辅助函数 ====================

/**
 * 上传图标到 S3
 * @param uploadUrl S3 预签名 URL
 * @param file 图片文件
 */
export async function uploadIconToS3(uploadUrl: string, file: File): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  })
}

/**
 * 上传头像到 S3（v11.2.2 新增）
 * @param uploadUrl S3 预签名 URL
 * @param file 图片文件
 */
export async function uploadAvatarToS3(uploadUrl: string, file: File): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  })
}
